/**
 * Credits Store — Zustand global state for credits, role, and user profile.
 * Role-aware: admin/tester get unlimited flag.
 */
import { create } from 'zustand';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WELCOME_BONUS_AMOUNT = 40;

export const useCreditsStore = create((set, get) => ({
  balance: null,
  totalSpent: 0,
  role: 'user',
  unlimited: false,
  userProfile: null, // { user_id, email, name, role, is_admin, is_tester, unlimited }
  costs: [],
  history: [],
  loading: false,
  costsLoaded: false,
  showBuyModal: false,
  bannerDismissed: false,
  welcomeBonusGranted: false,

  openBuyModal: () => set({ showBuyModal: true }),
  closeBuyModal: () => set({ showBuyModal: false }),
  dismissLowBalanceBanner: () => set({ bannerDismissed: true }),
  // Re-show the banner (e.g. after a successful purchase so it clears on low again)
  resetLowBalanceBanner: () => set({ bannerDismissed: false }),

  fetchBalance: async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await axios.get(`${API}/credits/balance`);
        const d = res.data;
        set({
          balance: d.balance,
          totalSpent: d.total_spent || 0,
          role: d.role || 'user',
          unlimited: false,
          userProfile: d.user || null,
          welcomeBonusGranted: !!d.welcome_bonus_granted,
        });
        // Celebrate the freemium welcome bonus the first time we see it.
        if (d.welcome_bonus_granted) {
          toast.success(
            `🎁 ¡Bienvenid@ a MediaLabs! Te regalamos ${WELCOME_BONUS_AMOUNT} créditos para que explores la plataforma.`,
            { duration: 8000, position: 'top-center' }
          );
        }
        return d.balance;
      } catch (err) {
        if (err.response?.status === 401 && attempt < 2) {
          await new Promise(r => setTimeout(r, 800));
          continue;
        }
        console.error('[Credits] Balance fetch error:', err.message);
        return get().balance;
      }
    }
  },

  fetchCosts: async () => {
    if (get().costsLoaded) return get().costs;
    try {
      const res = await axios.get(`${API}/credits/costs`);
      const costs = res.data.costs || [];
      set({ costs, costsLoaded: true });
      return costs;
    } catch (err) {
      console.error('[Credits] Costs fetch error:', err.message);
      return [];
    }
  },

  fetchHistory: async (limit = 30) => {
    try {
      const res = await axios.get(`${API}/credits/history`, { params: { limit } });
      const history = res.data.history || [];
      set({ history });
      return history;
    } catch (err) {
      console.error('[Credits] History fetch error:', err.message);
      return [];
    }
  },

  getCostForType: (resultType) => {
    const { costs } = get();
    const entry = costs.find(c => c.result_type === resultType);
    return entry ? entry.credits : 1;
  },

  // Envío 4 · precio dinámico: el precio que ve el usuario ANTES de ejecutar
  // sale SIEMPRE de /credits/quote (con el flag apagado devuelve el fijo).
  // Cache de 60 s por (tipo, params): el debounce lo hace el llamador.
  quotes: {},

  fetchQuote: async (resultType, params = {}) => {
    const key = `${resultType}:${JSON.stringify(params)}`;
    const cached = get().quotes[key];
    if (cached && Date.now() - cached.at < 60000) return cached.quote;
    try {
      const res = await axios.post(`${API}/credits/quote`, {
        result_type: resultType,
        params,
      });
      const quote = res.data.quote || null;
      set((s) => ({ quotes: { ...s.quotes, [key]: { quote, at: Date.now() } } }));
      return quote;
    } catch (err) {
      console.error('[Credits] Quote error:', err.message);
      return null;
    }
  },

  hasEnoughCredits: (resultType) => {
    const { balance, costs } = get();
    if (balance === null) return true; // not loaded yet — optimistic
    const cost = costs.find(c => c.result_type === resultType);
    return balance >= (cost ? cost.credits : 1);
  },

  refreshAfterExecution: async () => {
    const [balance] = await Promise.all([
      get().fetchBalance(),
      get().fetchHistory(),
    ]);
    return balance;
  },
}));
